import React, { useState } from 'react';
import { Mic, Activity, Volume2, Play, CheckCircle2, RefreshCw, BarChart2 } from 'lucide-react';

export const SpeechTherapyModule: React.FC = () => {
  const [activeExercise, setActiveExercise] = useState('r_sound');
  const [recording, setRecording] = useState(false);
  const [score, setScore] = useState<number | null>(null);

  const exercises = [
    { id: 'r_sound', target: '/r/ Vocalic Articulation', phrase: 'The red rabbit ran around the rock.', difficulty: 'Medium' },
    { id: 's_sound', target: '/s/ Sibilant Clarity', phrase: 'Sam saw seven suns shining bright.', difficulty: 'Easy' },
    { id: 'th_sound', target: '/θ/ Interdental Fricative', phrase: 'Thirty-three thinkers thought deeply.', difficulty: 'Hard' },
  ];

  const handleStartTest = () => {
    setRecording(true);
    setScore(null);
    setTimeout(() => {
      setRecording(false);
      setScore(Math.floor(Math.random() * 15) + 85); // 85% to 99% accuracy
    }, 2500);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
      <div className="flex items-center justify-between border-b border-slate-100 pb-4">
        <div>
          <span className="px-2.5 py-0.5 bg-teal-100 text-teal-800 text-[10px] font-bold rounded-full uppercase tracking-wider">
            Saltillo & Everyday Speech Benchmark
          </span>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Speech & Articulation Therapy</h2>
          <p className="text-xs text-slate-500">Phonetic pitch analyzer, vocal exercise drills, and accuracy scoring</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Exercise Selector */}
        <div className="space-y-3">
          <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Target Articulation Exercises</h3>
          {exercises.map((item) => (
            <div
              key={item.id}
              onClick={() => setActiveExercise(item.id)}
              className={`p-3.5 rounded-xl border cursor-pointer transition ${
                activeExercise === item.id 
                  ? 'border-teal-500 bg-teal-50/50 shadow-sm' 
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-900">{item.target}</span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-700">
                  {item.difficulty}
                </span>
              </div>
              <p className="text-xs text-slate-600 mt-1.5 italic">"{item.phrase}"</p>
            </div>
          ))}
        </div>

        {/* Live Audio Analyzer Visualizer */}
        <div className="md:col-span-2 bg-slate-900 text-white rounded-xl p-6 flex flex-col justify-between space-y-6">
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-teal-400 flex items-center gap-1.5">
              <Activity className="w-4 h-4 animate-pulse" />
              Acoustic Pitch Spectrogram Analyzer
            </span>
            <span className="text-xs text-slate-400">Microphone: Ready</span>
          </div>

          {/* Simulated Waveform Canvas */}
          <div className="h-32 bg-slate-950 rounded-lg border border-slate-800 p-4 flex items-center justify-center gap-1">
            {recording ? (
              Array.from({ length: 32 }).map((_, idx) => (
                <div 
                  key={idx} 
                  className="w-1.5 bg-teal-400 rounded-full animate-bounce"
                  style={{ 
                    height: `${Math.random() * 80 + 10}%`,
                    animationDelay: `${idx * 0.05}s` 
                  }}
                />
              ))
            ) : (
              <div className="text-slate-500 text-xs flex items-center gap-2">
                <Mic className="w-5 h-5 text-teal-400" />
                <span>Click "Record Vocal Trial" to capture audio waveform</span>
              </div>
            )}
          </div>

          {/* Controls & Scoring Result */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={handleStartTest}
              disabled={recording}
              className="px-6 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg text-xs flex items-center gap-2 transition"
            >
              <Mic className="w-4 h-4" />
              {recording ? 'Analyzing Pitch Frequency...' : 'Record Vocal Trial'}
            </button>

            {score !== null && (
              <div className="flex items-center gap-3 bg-slate-800 px-4 py-2 rounded-lg border border-slate-700">
                <BarChart2 className="w-5 h-5 text-emerald-400" />
                <div>
                  <div className="text-xs text-slate-400">Articulation Accuracy</div>
                  <div className="text-lg font-black text-emerald-400">{score}% Score</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
